import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone,
  PhoneOff,
  PhoneIncoming,
  PhoneMissed,
  Mic,
  MicOff,
  AlertCircle,
  LayoutDashboard,
  LogOut,
  Voicemail,
  History,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useSoftphone from '../../softphone/useSoftphone';
import VoicemailList from '../../softphone/VoicemailList';
import freshroadLogo from '../../images/freshroadlogo.png';

const formatDuration = (totalSeconds) => {
  const mins = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const secs = String(totalSeconds % 60).padStart(2, '0');
  return `${mins}:${secs}`;
};

const formatCaller = (raw) => {
  if (!raw) return 'Unknown caller';
  const digits = String(raw).replace(/[^\d+]/g, '');
  const match = digits.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (match) return `(${match[1]}) ${match[2]}-${match[3]}`;
  return digits || 'Unknown caller';
};

const formatTime = (date) =>
  date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const OUTCOMES = {
  answered: { label: 'Answered', className: 'bg-emerald-50 text-emerald-700', Icon: Phone },
  declined: { label: 'Sent to voicemail', className: 'bg-slate-100 text-slate-600', Icon: PhoneOff },
  missed: { label: 'Missed → voicemail', className: 'bg-amber-50 text-amber-700', Icon: PhoneMissed },
  failed: { label: 'Failed', className: 'bg-rose-50 text-rose-700', Icon: AlertCircle },
};

const StatusPill = ({ status }) => {
  const map = {
    ready: { text: 'Available', dot: 'bg-emerald-500', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    ringing: { text: 'Incoming call', dot: 'bg-cyan-500 animate-pulse', className: 'border-cyan-200 bg-cyan-50 text-cyan-700' },
    active: { text: 'On a call', dot: 'bg-cyan-500', className: 'border-cyan-200 bg-cyan-50 text-cyan-700' },
    error: { text: 'Unavailable', dot: 'bg-rose-500', className: 'border-rose-200 bg-rose-50 text-rose-700' },
    offline: { text: 'Connecting…', dot: 'bg-slate-400', className: 'border-slate-200 bg-slate-50 text-slate-600' },
  };
  const state = map[status] || map.offline;
  return (
    <div className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${state.className}`}>
      <span className={`h-2 w-2 rounded-full ${state.dot}`} />
      {state.text}
    </div>
  );
};

const SupportDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('calls');
  const { status, caller, muted, seconds, error, history, answer, decline, hangUp, toggleMute } = useSoftphone();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const ringing = status === 'ringing';
  const active = status === 'active';

  return (
    <div className="demo-ui flex h-screen flex-col overflow-hidden bg-[#F7F9FB] text-slate-900">
      <header className="flex h-[72px] shrink-0 items-center justify-between border-b border-slate-200 bg-white px-6">
        <div className="flex items-center gap-3">
          <img src={freshroadLogo} alt="Freshroad" className="h-8 w-auto" />
          <div>
            <div className="text-sm font-semibold leading-tight">Support Desk</div>
            <div className="text-xs text-slate-500">Live phone support</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill status={status} />
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
          >
            <LayoutDashboard className="h-4 w-4" />
            Main dashboard
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </header>

      {error ? (
        <div className="flex shrink-0 items-start gap-2 border-b border-rose-100 bg-rose-50 px-6 py-2.5 text-sm text-rose-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 p-6 lg:grid-cols-[1fr_360px]">
        <section className="flex min-h-0 flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-8">
          {ringing || active ? (
            <div className="flex w-full max-w-md flex-col items-center text-center">
              <div
                className={`flex h-20 w-20 items-center justify-center rounded-full ${
                  ringing ? 'animate-pulse bg-cyan-100 text-cyan-700' : 'bg-emerald-100 text-emerald-700'
                }`}
              >
                <PhoneIncoming className="h-9 w-9" />
              </div>
              <div className="mt-5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {ringing ? 'Incoming call' : 'On a call'}
              </div>
              <div className="mt-1 text-3xl font-semibold tracking-tight">{formatCaller(caller)}</div>
              <div className="mt-2 text-sm text-slate-500">
                {ringing ? 'Answer to talk through your browser' : formatDuration(seconds)}
              </div>

              <div className="mt-8 flex w-full items-center justify-center gap-3">
                {ringing ? (
                  <>
                    <button
                      type="button"
                      onClick={answer}
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
                    >
                      <Phone className="h-4 w-4" />
                      Answer
                    </button>
                    <button
                      type="button"
                      onClick={decline}
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-rose-600 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-rose-700"
                    >
                      <PhoneOff className="h-4 w-4" />
                      Busy
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={toggleMute}
                      className={`flex items-center justify-center gap-2 rounded-xl border px-6 py-3.5 text-sm font-semibold transition ${
                        muted
                          ? 'border-slate-800 bg-slate-800 text-white hover:bg-slate-900'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                      {muted ? 'Unmute' : 'Mute'}
                    </button>
                    <button
                      type="button"
                      onClick={hangUp}
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-rose-600 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-rose-700"
                    >
                      <PhoneOff className="h-4 w-4" />
                      Hang up
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <Phone className="h-9 w-9" />
              </div>
              <div className="mt-5 text-lg font-semibold">
                {status === 'ready' ? 'Waiting for calls' : 'Phone not connected'}
              </div>
              <p className="mt-2 max-w-sm text-sm text-slate-500">
                {status === 'ready'
                  ? `Calls to your support number ring here while this page is open${user?.name ? `, ${user.name}` : ''}. Close it and callers go to voicemail.`
                  : 'Once the phone connects, incoming calls will appear here.'}
              </p>
            </div>
          )}
        </section>

        <aside className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="flex shrink-0 border-b border-slate-200">
            <button
              type="button"
              onClick={() => setTab('calls')}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition ${
                tab === 'calls'
                  ? 'border-b-2 border-cyan-600 text-cyan-700'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <History className="h-4 w-4" />
              Recent
            </button>
            <button
              type="button"
              onClick={() => setTab('voicemail')}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition ${
                tab === 'voicemail'
                  ? 'border-b-2 border-cyan-600 text-cyan-700'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Voicemail className="h-4 w-4" />
              Voicemail
            </button>
          </div>

          {tab === 'voicemail' ? (
            <VoicemailList refreshSignal={history.length} />
          ) : (
          <>
          <div className="shrink-0 border-b border-slate-100 px-5 py-3">
            <div className="text-xs text-slate-500">This session only</div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
            {history.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-slate-400">No calls yet</div>
            ) : (
              history.map((entry) => {
                const outcome = OUTCOMES[entry.outcome] || OUTCOMES.failed;
                const { Icon } = outcome;
                return (
                  <div key={entry.id} className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-slate-50">
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${outcome.className}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{formatCaller(entry.from)}</div>
                      <div className="text-xs text-slate-500">
                        {outcome.label}
                        {entry.outcome === 'answered' && entry.duration > 0 ? ` · ${formatDuration(entry.duration)}` : ''}
                      </div>
                    </div>
                    <div className="shrink-0 text-xs text-slate-400">{formatTime(entry.at)}</div>
                  </div>
                );
              })
            )}
          </div>
          </>
          )}
        </aside>
      </div>
    </div>
  );
};

export default SupportDashboard;
